import Menu from "@components/Menu";
import Nav from "@components/Navigater";
import {
  ThemeEnum,
  ThemeProvider,
} from "@components/ThemeProvider/ThemeProvider";
import Search from "@src/app/components/Search";
import { useHeaderScroll } from "@src/app/hooks/useHeaderScroll";
import useIsSmallScreen from "@src/app/hooks/useIsSmallScreen";
import clsx from "clsx";
import type { ReactElement } from "react";
import { useEffect, useState } from "react";

import HeaderActions from "./HeaderActions";
import HeaderLogo from "./HeaderLogo";
import MenuButton from "./MenuButton";
import ShareAntsToggle from "./ShareAntsToggle";

export const navList = [
  {
    name: "观点",
    path: "/perspective?ref=top-menubar",
  },
  {
    name: "新闻",
    path: "/overview?ref=top-menubar",
  },
  {
    name: "特写",
    path: "/good-reads?ref=top-menubar",
  },
  {
    name: "视频",
    path: "/videos?ref=top-menubar",
  },
];

export const menuList = [
  {
    name: "首页",
    path: "/",
  },
  ...navList,
];

export type ThemeProps = {
  theme?: ThemeEnum;
  showShare?: boolean;
};

export default function Header({
  theme = ThemeEnum.light,
  showShare = false,
}: ThemeProps): ReactElement {
  const [menuShow, setMenuShow] = useState<boolean>(false);
  const [isVisible, setIsVisible] = useState<boolean>(false);
  const isSmallScreen = useIsSmallScreen();
  const { isScrolled, isHeaderSticky } = useHeaderScroll();

  const handleMenuShow = () => {
    setMenuShow(!menuShow);
  };

  const setOverlay = () => {
    setIsVisible(!isVisible);
  };

  const handleSearch = (query: string) => {
    const trimmedQuery = query.trim();
    if (!trimmedQuery) return;
    window.location.href = `/sitesearch?q=${encodeURIComponent(trimmedQuery)}#gsc.tab=0&gsc.q=${encodeURIComponent(trimmedQuery)}&gsc.page=1`;
  };

  useEffect(() => {
    if (menuShow && isSmallScreen) {
      document.documentElement.style.overflow = "hidden";
      document.documentElement.style.height = "100vh";
    }
    return () => {
      document.documentElement.style.overflow = "";
      document.documentElement.style.height = "";
    };
  }, [menuShow, isSmallScreen]);

  return (
    <ThemeProvider theme={theme}>
      <section
        id="header"
        className={clsx(
          "w-full px-[4px] shadow-[3px_3px_5px_0px_rgba(0,0,0,0.07)] lg:px-[24px]",
          "sticky top-0 z-[100001]",
          theme === ThemeEnum.light ? "bg-secondary" : "bg-inverse",
        )}
        data-testid="redants-header"
      >
        <div className="mx-auto box-border max-w-[1200px] px-[12px]">
          <div className="flex items-center justify-between gap-[16px]">
            {/* 左侧区域：Share/Ants切换 + 移动端菜单按钮 */}
            <div
              className={clsx(
                "flex h-[58px] flex-1 items-center py-[8px] lg:h-[100px] lg:py-0",
                "transition-[height] duration-200 ease-out",
                isHeaderSticky && !isSmallScreen && "!h-[48px]",
              )}
            >
              <ShareAntsToggle isHeaderSticky={isHeaderSticky} />
              <MenuButton
                theme={theme}
                menuShow={menuShow}
                onClick={handleMenuShow}
              />
            </div>

            {/* 中间区域：Logo */}
            <div
              className={clsx(
                "flex h-[58px] flex-1 items-center justify-center py-[8px] lg:h-[100px] lg:py-0",
                "transition-[height] duration-200 ease-out",
                showShare && "transition-opacity duration-300",
                showShare && isScrolled && "max-lg:hidden",
                isHeaderSticky && !isSmallScreen && "!h-[48px]",
              )}
            >
              <HeaderLogo
                theme={theme}
                isHeaderSticky={isHeaderSticky}
                isSmallScreen={isSmallScreen}
                navList={navList}
              />
            </div>

            {/* 右侧区域：搜索按钮 + 投稿按钮 + 移动端社交分享 */}
            <HeaderActions
              theme={theme}
              isHeaderSticky={isHeaderSticky}
              showShare={showShare}
              isScrolled={isScrolled}
              isSmallScreen={isSmallScreen}
              onSearchClick={setOverlay}
            />
          </div>

          {/* PC端导航栏（非吸顶状态） */}
          {!isHeaderSticky && (
            <div className="hidden h-[40px] items-center justify-center lg:flex">
              <Nav navList={navList} />
            </div>
          )}
        </div>
      </section>

      {/* 移动端菜单 */}
      <section className="visible lg:hidden">
        <Menu visible={menuShow} />
      </section>

      {/* 搜索组件 */}
      <Search
        onSearch={handleSearch}
        setOverlay={setOverlay}
        isVisible={isVisible}
      />
    </ThemeProvider>
  );
}
